import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';

const ELLIPSIS_RESERVE_EM = 1.25;

interface HighlightSegment {
  text: string;
  isMatch: boolean;
}

interface HighlightParts {
  prefix: string;
  gap: string;
  tail: HighlightSegment[];
}

function indexOfTerm(value: string, term: string): number {
  return value.toLowerCase().indexOf(term.toLowerCase());
}

function buildSegments(value: string, term: string): HighlightSegment[] {
  if (!term || !value) {
    return [{ text: value, isMatch: false }];
  }

  const haystack = value.toLowerCase();
  const needle = term.toLowerCase();
  const segments: HighlightSegment[] = [];
  let cursor = 0;

  while (cursor < value.length) {
    const index = haystack.indexOf(needle, cursor);

    if (index === -1) {
      segments.push({ text: value.slice(cursor), isMatch: false });
      break;
    }

    if (index > cursor) {
      segments.push({ text: value.slice(cursor, index), isMatch: false });
    }

    segments.push({
      text: value.slice(index, index + term.length),
      isMatch: true,
    });

    cursor = index + term.length;
  }

  return segments;
}

function splitAtFirstMatch(value: string, term: string): HighlightParts {
  const start = indexOfTerm(value, term);
  const head = start === -1 ? '' : value.slice(0, start);
  const prefix = head.trimEnd();

  return {
    prefix,
    gap: head.slice(prefix.length),
    tail: buildSegments(value.slice(head.length), term),
  };
}

function exceedsEllipsisReserve(element: HTMLElement): boolean {
  const fontSize = parseFloat(getComputedStyle(element).fontSize);
  const text = document.createRange();
  text.selectNodeContents(element);

  return text.getBoundingClientRect().width > fontSize * ELLIPSIS_RESERVE_EM;
}

@Component({
  selector: 'procure-highlighted-text',
  templateUrl: './highlighted-text.component.html',
  styleUrl: './highlighted-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightedText {
  readonly value = input<string | null>(null);
  readonly term = input<string>('');

  protected readonly parts = computed(() =>
    splitAtFirstMatch(this.value() ?? '', this.term()),
  );

  protected readonly ellipsisReserveEm = signal(0);

  private readonly prefixText =
    viewChild<ElementRef<HTMLElement>>('prefixText');

  constructor() {
    afterRenderEffect(() => {
      const element = this.parts().prefix
        ? this.prefixText()?.nativeElement
        : undefined;
      const reserve = element && exceedsEllipsisReserve(element);

      this.ellipsisReserveEm.set(reserve ? ELLIPSIS_RESERVE_EM : 0);
    });
  }
}
